import { Component, Input } from '@angular/core';

import { TProject } from '@shared/interfaces/TProject';
import { computeMomentumLong, computeMomentumShort } from '@shared/utils/computeMomentum';
import { momentumLongDesc, momentumShortDesc } from 'src/app/constants/momentum-description.constant';
import { calculatePercent } from 'src/app/helpers/calculate-percent-helper';
import { CurrentBusinessTableData } from 'src/app/interfaces/current-business-table-data.interface';
import { YearMonth } from 'src/app/interfaces/year-month.interface';
import { KamReportsService } from './../../services/kam-reports.service';

@Component({
  selector: 'app-current-business',
  templateUrl: './current-business.component.html',
  styleUrls: ['./current-business.component.scss']
})
export class CurrentBusinessComponent {

  @Input() project: TProject;
  currentBusinessTableData: CurrentBusinessTableData;
  private closestDate: YearMonth;
  public momentumLongDesc: string = momentumLongDesc;
  public momentumShortDesc: string = momentumShortDesc;

  constructor(
    private _kamReportsService: KamReportsService,
  ) { }

  ngOnInit(): void {
    // Simulate fetching data from a service
    this.closestDate = this._kamReportsService.getClosestSalesDate(this.project.accountBricks);
    this.currentBusinessTableData = this.buildCurrentBusinessTableData$(this.project);
  }

  buildCurrentBusinessTableData$(project: TProject): CurrentBusinessTableData {
    const nationalSalesTotal = this._kamReportsService.getTotalFromClosestMonth(project.accountBricks, 'sales');
    const nationalSalesBudget = this._kamReportsService.sumAllPlannedBudgets(project.accountRegions, this.closestDate.year);
    const nationalSalesPercent = calculatePercent(+nationalSalesTotal, +nationalSalesBudget);
    const nationalSalesMomentumShort = computeMomentumShort(this._kamReportsService.getLastXMonthsTotal(project.accountBricks, 16, 'sales'));
    const nationalSalesMomentumLong = computeMomentumLong(this._kamReportsService.getLastXMonthsTotal(project.accountBricks, 16, 'sales'));

    const nationalSalesInEuroTotal = this._kamReportsService.getTotalFromClosestMonth(project.accountBricks, 'salesInEuro');
    const nationalSalesInEuroBudget = this._kamReportsService.sumAllPlannedBudgets(project.accountRegions, this.closestDate.year);
    const nationalSalesInEuroPercent = calculatePercent(+nationalSalesInEuroTotal, +nationalSalesInEuroBudget);
    const nationalSalesInEuroMomentumShort = computeMomentumShort(this._kamReportsService.getLastXMonthsTotal(project.accountBricks, 16, 'salesInEuro'));
    const nationalSalesInEuroMomentumLong = computeMomentumLong(this._kamReportsService.getLastXMonthsTotal(project.accountBricks, 16, 'salesInEuro'));

    const patientsMomentumShort = computeMomentumShort(this._kamReportsService.getLastXMonthsTotal(project.accountBricks, 16, 'patients'));
    const patientsMomentumLong = computeMomentumLong(this._kamReportsService.getLastXMonthsTotal(project.accountBricks, 16, 'patients'));

    const patientsTotal = this._kamReportsService.getTotalFromClosestMonth(project.accountBricks, 'patients');
    const patientsFourMonth = Math.round(patientsMomentumShort);
    const patientsPy = Math.round(patientsMomentumLong);

    const result: CurrentBusinessTableData = {
      nationalSales: {
        total:  isNaN(+nationalSalesTotal) ? 'N/A' : Math.round(nationalSalesTotal),
        deltaPY: isNaN(+nationalSalesMomentumLong) ? 'N/A' : Math.round(nationalSalesMomentumLong) + '%',
        delta4Month: isNaN(+nationalSalesMomentumShort) ? 'N/A' : Math.round(nationalSalesMomentumShort) + '%',
        budget: isNaN(+nationalSalesBudget) ? 'N/A' : Math.round(nationalSalesBudget),
        percentage: nationalSalesPercent,
      },
      nationalSalesInEuro: {
        total:  isNaN(+nationalSalesInEuroTotal) ? 'N/A' : new Intl.NumberFormat().format(Math.round(nationalSalesInEuroTotal)),
        deltaPY: isNaN(+nationalSalesInEuroMomentumLong) ? 'N/A' : Math.round(nationalSalesInEuroMomentumLong) + '%',
        delta4Month: isNaN(+nationalSalesInEuroMomentumShort) ? 'N/A' : Math.round(nationalSalesInEuroMomentumShort) + '%',
        budget: isNaN(+nationalSalesInEuroBudget) ? 'N/A' : Math.round(nationalSalesInEuroBudget),
        percentage: nationalSalesInEuroPercent,
      },
      patients: {
        total: patientsTotal,
        deltaPY: isNaN(+patientsPy) ? 'N/A' : Math.round(patientsPy) + '%',
        delta4Month: isNaN(+patientsFourMonth) ? 'N/A' : Math.round(patientsFourMonth) + '%',
      },
      patientsICD: {
        total: 0,
        deltaPY: 0,
      }
    };

    return result;
  }


}
